import useFetchApi, { isLoading } from 'hooks/useFetchApi/useFetchApi'
import useDefaultDevice from 'hooks/useDefaultDevice/useDefaultDevice'
import useReceiptTemplate from 'hooks/useReceiptTemplate/useReceiptTemplate'
import { isUndefined } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import paymentService from 'Services/api/payment.service'
import receiptSettingService from 'Services/api/receiptSetting.service'
import { showErrorMessage } from 'store/actions/ui.action'
import { useAppSelector } from 'store/useStoreHook'
import timeoutHelper from 'utilities/timeoutHelper'
import { ReceiptTemplateMarkupSlimModel } from 'models'
import printerService from 'Services/api/printer.service'
import { sendPrintCommandToAndroidPrintingApp } from 'utilities/androidPrinting'
import useCurrentTerminalDevicesHwAgentCaller from 'hooks/useHardwareAgentCaller/useCurrentTerminalDevicesHwAgentCaller'
import { logHWError } from 'utilities/logError'

interface UseReceiptParams {
    saleID?: string
    defaultSellReceiptTemplate?: ReceiptTemplateMarkupSlimModel
}
const APICONFIG = { showErrorToast: true }
export function useReceipt({
    saleID,
    defaultSellReceiptTemplate,
}: UseReceiptParams) {
    const dispatch = useDispatch()
    const [, sendPaymentReceiptReport, sendingStatus] = useFetchApi(
        paymentService.sendPaymentReceiptReport,
        {
            showSuccessToast: true,
            showErrorToast: true,
            successMessage: 'Email has been sent.',
        },
    )
    const [receiptData, getReceiptBindingData, getingStatus] = useFetchApi(
        receiptSettingService.getReceiptDataBinding,
    )

    const [isPrinting, setIsPrinting] = useState(false)

    const isReceiptReady = useMemo(() => {
        return receiptData && !isLoading([getingStatus])
    }, [getingStatus, receiptData])

    const {
        previewSvg,
        parseReceiptTemplateImage,
        parsePrintingCommand,
    } = useReceiptTemplate()

    const hardwareAgent = useCurrentTerminalDevicesHwAgentCaller()
    const { enableOnScreenPrinter, enableAndroidPrinting } = useDefaultDevice()

    const tenantInfo = useAppSelector(
        (state) => state.tenantIdentifierInfo.data,
    )
    const [printerStatus = false, getPrinterStatus] = useFetchApi(
        printerService.checkPrinterStatus,
        APICONFIG,
    )

    const onPrintReceipt = useCallback(
        async (callerName: string, isDuplicateReceipt: boolean = false) => {
            try {
                if (
                    !isUndefined(defaultSellReceiptTemplate) &&
                    isReceiptReady
                ) {
                    setIsPrinting(true)

                    if (enableAndroidPrinting) {
                        const command = await parsePrintingCommand({
                            markup:
                                defaultSellReceiptTemplate.receiptMarkup || '',
                            data: receiptData!,
                            isDuplicateReceipt,
                        })

                        sendPrintCommandToAndroidPrintingApp(command)
                    } else if (!enableOnScreenPrinter) {
                        const command = await parsePrintingCommand({
                            markup:
                                defaultSellReceiptTemplate.receiptMarkup || '',
                            data: receiptData!,
                            isDuplicateReceipt,
                        })
                        await hardwareAgent.print(command, 'receipt')
                        await timeoutHelper(1500)
                    }
                } else {
                    dispatch(
                        showErrorMessage(
                            "Can't find default template for sale receipt",
                        ),
                    )
                }
            } catch (error) {
                logHWError(callerName, 'onPrintReceipt', error)
            } finally {
                setIsPrinting(false)
            }
        },
        [
            isReceiptReady,
            parsePrintingCommand,
            defaultSellReceiptTemplate,
            receiptData,
            enableOnScreenPrinter,
            enableAndroidPrinting,
            hardwareAgent,
            dispatch,
        ],
    )

    const sendReportToEmail = useCallback(
        async (email: string, content: string) => {
            await sendPaymentReceiptReport({
                email: email,
                subject: `E-Receipt from ${tenantInfo.companyName} ${tenantInfo.locationName}`,
                reportBody: `<img alt="Payment Receipt Report" src="payment_receipt.png" />`,
                base64Attachments: [
                    {
                        fileName: 'payment_receipt.png',
                        content: content.substring(22),
                    },
                ],
            })
        },
        [sendPaymentReceiptReport, tenantInfo],
    )

    const parseReportTemplate = useCallback(
        (isDuplicateReceipt: boolean = false) => {
            if (!isUndefined(defaultSellReceiptTemplate) && isReceiptReady) {
                parseReceiptTemplateImage({
                    markup: defaultSellReceiptTemplate.receiptMarkup || '',
                    data: receiptData!,
                    isDuplicateReceipt,
                })
            } else {
                dispatch(
                    showErrorMessage(
                        "Can't find default template for sale receipt",
                    ),
                )
            }
        },
        [
            isReceiptReady,
            parseReceiptTemplateImage,
            defaultSellReceiptTemplate,
            receiptData,
            dispatch,
        ],
    )

    useEffect(() => {
        if (saleID) {
            getPrinterStatus()
            getReceiptBindingData(saleID)
        }
    }, [
        getReceiptBindingData,
        saleID,
        getPrinterStatus,
        defaultSellReceiptTemplate?.receiptTemplateTypeID,
    ])

    return {
        printerStatus,
        onPrintReceipt,
        sendReportToEmail,
        isPrinting,
        previewSvg,
        parseReportTemplate,
        sendingStatus,
        enableOnScreenPrinter,
        isReceiptReady,
    }
}
